import React, { useState, useEffect, useMemo } from "react";
import {
  Table, Container, Row, Col, Form, Button, ButtonGroup, Dropdown,
  OverlayTrigger, Tooltip, Modal, Offcanvas, Accordion, Badge, Spinner
} from "react-bootstrap";
import { getUserPracticeById } from "../../api";
import { getSalesReportByPracticeNumbers } from "../../afinanceapi";
import { jwtDecode } from "jwt-decode";
import { useLocation, useNavigate } from "react-router-dom";
import { saveAs } from "file-saver";
import "./CreateInvoice.css";
import "./acc-list.css";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function TransactionList() {
  const [invoices, setInvoices] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totals, setTotals] = useState({ sumExcl: 0, sumVat: 0, sumTotal: 0 });
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const [practiceList, setPracticeList] = useState([]);

  // Committed filters (drive the table)
  const [selectedPractices, setSelectedPractices] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Temporary filters while panel is open
  const [tmpPractices, setTmpPractices] = useState([]);
  const [tmpStatuses, setTmpStatuses] = useState([]);
  const [tmpStartDate, setTmpStartDate] = useState("");
  const [tmpEndDate, setTmpEndDate] = useState("");

  const [page, setPage] = useState(1);
  const limit = 20;

  const [expandedRows, setExpandedRows] = useState(new Set());
  const [exportLoading, setExportLoading] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const query = useQuery();

  const currency = new Intl.NumberFormat("en-ZA", { style: "currency", currency: "ZAR" });

  const statusOptions = ["Posted", "Partially Paid", "Paid", "Cancelled", "Written Off"];

  // Init filters from URL params on mount; fall back to last 1 month default
  useEffect(() => {
    const p = query.get('practices');
    const s = query.get('statuses');
    const st = query.get('start');
    const en = query.get('end');

    if (st || en || p || s) {
      // Restore from URL params
      const practices = p ? p.split(',') : [];
      const statuses = s ? s.split(',') : [];
      setSelectedPractices(practices);
      setSelectedStatuses(statuses);
      setTmpPractices(practices);
      setTmpStatuses(statuses);
      if (st) { setStartDate(st); setTmpStartDate(st); }
      if (en) { setEndDate(en); setTmpEndDate(en); }
    } else {
      // Default: last 1 month
      const today = new Date();
      const start = new Date();
      start.setMonth(start.getMonth() - 1);
      const iso = (d) => d.toISOString().slice(0, 10);
      setStartDate(iso(start));
      setEndDate(iso(today));
      setTmpStartDate(iso(start));
      setTmpEndDate(iso(today));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch practices
  useEffect(() => {
    async function fetchPractices() {
      try {
        const token = sessionStorage.getItem("user");
        if (!token) throw new Error("No user token found");
        const user = jwtDecode(token);
        const practices = await getUserPracticeById(user._id);
        setPracticeList(practices || []);
      } catch (e) {
        console.error("Error fetching practices:", e);
        setPracticeList([]);
      }
    }
    fetchPractices();
  }, []);

  // Backend-driven fetch — fires on every filter/sort/page change
  useEffect(() => {
    if (!startDate || !endDate || practiceList.length === 0) return;

    async function fetchData() {
      try {
        setLoading(true);
        // Map selected practice names → practice numbers (all if none selected)
        const practiceNums =
          selectedPractices.length > 0
            ? practiceList
                .filter((p) => selectedPractices.includes(p.practiceName))
                .map((p) => p.practiceNumber)
            : practiceList.map((p) => p.practiceNumber);

        const result = await getSalesReportByPracticeNumbers({
          practiceNumbers: practiceNums,
          startDate,
          endDate,
          statuses: selectedStatuses,
          sortKey: sortConfig.key,
          sortDir: sortConfig.direction,
          page,
          limit,
        });
        setInvoices(result.data || []);
        setTotalCount(result.total || 0);
        setTotals(result.totals || { sumExcl: 0, sumVat: 0, sumTotal: 0 });
        setExpandedRows(new Set());
      } catch (e) {
        console.error("Error fetching invoices:", e);
        setInvoices([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [practiceList, selectedStatuses, selectedPractices, startDate, endDate, sortConfig, page]);

  const allPractices = useMemo(
    () => (practiceList || []).map((p) => p.practiceName).filter(Boolean),
    [practiceList]
  );

  const toggleIn = (arr, value) =>
    arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];

  // Derive display rows from paged backend data
  const displayRows = useMemo(() => invoices.map((inv) => ({ type: "invoice", inv })), [invoices]);

  const totalPages = Math.max(1, Math.ceil(totalCount / limit));

  const toggleRow = (id) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
    setPage(1);
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? (
      <i className="fa-solid fa-arrow-up text-primary ms-2" />
    ) : (
      <i className="fa-solid fa-arrow-down text-primary ms-2" />
    );
  };

  // exportType: "full" (invoice headers + lines + credits + write-offs), "summary"
  // (invoice header rows only), or "lines" (line rows only).
  const handleExportExcel = async (exportType = "full") => {
    setShowExportModal(false);
    try {
      setExportLoading(true);
      const practiceNums =
        selectedPractices.length > 0
          ? practiceList
              .filter((p) => selectedPractices.includes(p.practiceName))
              .map((p) => p.practiceNumber)
          : practiceList.map((p) => p.practiceNumber);

      const result = await getSalesReportByPracticeNumbers({
        practiceNumbers: practiceNums,
        startDate,
        endDate,
        statuses: selectedStatuses,
        sortKey: sortConfig.key,
        sortDir: sortConfig.direction,
        page: 1,
        limit: 99999,
      });

      const allInvoices = result.data || [];

      // Layout: one Invoice header row (with Excl/VAT/Total) followed by Line /
      // Credit Note / Write-Off detail rows. The Excl/VAT/Total columns appear only
      // on the Invoice header rows so summing them in Excel reconciles to the
      // on-screen totals.
      const exportData = allInvoices.reduce((acc, inv) => {
        const invDate = inv.invoiceDate ? new Date(inv.invoiceDate).toISOString().slice(0, 10) : "";
        const practiceName = inv.practice?.practiceName || "";
        const patientName = inv.patient?.patientFirstName
          ? `${inv.patient.patientFirstName} ${inv.patient.patientLastName || ""}`.trim()
          : "";
        const lines = Array.isArray(inv.lines) ? inv.lines : [];
        const receipts = Array.isArray(inv.receipts) ? inv.receipts : [];
        const credits = receipts.filter((r) => r.creditNoteNumber);
        const writeOffs = receipts.filter((r) => r.writeOffNumber);

        const totalCN = credits.reduce((s, r) => s + (Number(r.creditNoteAmount) || 0), 0);
        const totalWO = writeOffs.reduce((s, r) => s + (Number(r.writeOffAmount) || 0), 0);
        const adjustedTotal = (Number(inv.totalAmount) || 0) - totalCN - totalWO;

        // VAT is 15% in SA and only applies when the practice has a VAT number;
        // mirrors the backend salesReport hasVat check (invoiceRoutes.js:2576).
        const hasVat = !!inv.practice?.vatNumber;
        const splitVat = (incl) => {
          const i = Number(incl) || 0;
          if (!hasVat) return { excl: i, vat: 0 };
          const excl = i / 1.15;
          return { excl, vat: i - excl };
        };

        // 1. Invoice header row — carries Excl/VAT/Total
        acc.push({
          RowType: "Invoice",
          Date: invDate,
          DateOfService: "",
          InvoiceNumber: inv.invoiceNumber || "",
          AccountNumber: inv.accountNumber || "",
          AccountName: inv.accountName || "",
          Status: inv.status || "",
          Reference: "",
          Description: "Invoice summary",
          Quantity: "",
          Rate: "",
          ExclAmount: Number(inv.exclAmount) || 0,
          VatAmount: Number(inv.vatAmount) || 0,
          Total: adjustedTotal,
          Patient: patientName,
          ServiceProvider: "",
          PracticeName: practiceName,
        });

        // 2. Line rows — line.rate is the line total (incl VAT if VAT-registered);
        // CreateInvoice sums line.rate directly into totalAmount with no qty multiplier.
        lines.forEach((line) => {
          const sp = line.serviceProvider
            ? `${line.serviceProvider.firstName} ${line.serviceProvider.lastName}`.trim()
            : inv.practitioner?.firstName
              ? `${inv.practitioner.firstName} ${inv.practitioner.lastName || ""}`.trim()
              : "";
          // Date of service: same precedence as CreateInvoice's getLineServiceDate —
          // custom date > appointment-derived date > legacy > invoice date.
          const serviceDate = String(
            line.appointmentDateCustom ||
              line.appointmentDateSet ||
              line.appointmentDate ||
              inv.invoiceDate ||
              ""
          ).slice(0, 10);
          const lineIncl = Number(line.rate) || 0;
          const lineSplit = splitVat(lineIncl);
          acc.push({
            RowType: "Line",
            Date: invDate,
            DateOfService: serviceDate,
            InvoiceNumber: inv.invoiceNumber || "",
            AccountNumber: inv.accountNumber || "",
            AccountName: inv.accountName || "",
            Status: "",
            Reference: line.tariffCode || line.nappiCode || "",
            Description: line.description || "",
            Quantity: Number(line.quantity ?? 1),
            Rate: lineIncl,
            ExclAmount: lineSplit.excl,
            VatAmount: lineSplit.vat,
            Total: lineIncl,
            Patient: patientName,
            ServiceProvider: sp,
            PracticeName: practiceName,
          });
        });

        // 3. Credit notes — prefer stored creditNoteExclAmount/VATAmount when
        // present (issued by the Credit Note flow); else fall back to splitting.
        credits.forEach((r) => {
          const cnIncl = -(Number(r.creditNoteAmount) || 0);
          let cnExcl;
          let cnVat;
          if (
            hasVat &&
            r.creditNoteExclAmount != null &&
            r.creditNoteVATAmount != null
          ) {
            cnExcl = -(Number(r.creditNoteExclAmount) || 0);
            cnVat = -(Number(r.creditNoteVATAmount) || 0);
          } else {
            const s = splitVat(cnIncl);
            cnExcl = s.excl;
            cnVat = s.vat;
          }
          acc.push({
            RowType: "Credit Note",
            Date: r.creditNoteDate ? new Date(r.creditNoteDate).toISOString().slice(0, 10) : invDate,
            DateOfService: "",
            InvoiceNumber: inv.invoiceNumber || "",
            AccountNumber: inv.accountNumber || "",
            AccountName: inv.accountName || "",
            Status: "",
            Reference: r.creditNoteNumber || "",
            Description: "Credit Note",
            Quantity: "",
            Rate: cnIncl,
            ExclAmount: cnExcl,
            VatAmount: cnVat,
            Total: cnIncl,
            Patient: patientName,
            ServiceProvider: "",
            PracticeName: practiceName,
          });
        });

        // 4. Write-offs
        writeOffs.forEach((r) => {
          const woIncl = -(Number(r.writeOffAmount) || 0);
          const woSplit = splitVat(woIncl);
          acc.push({
            RowType: "Write-Off",
            Date: r.writeOffDate ? new Date(r.writeOffDate).toISOString().slice(0, 10) : invDate,
            DateOfService: "",
            InvoiceNumber: inv.invoiceNumber || "",
            AccountNumber: inv.accountNumber || "",
            AccountName: inv.accountName || "",
            Status: "",
            Reference: r.writeOffNumber || "",
            Description: "Write-Off",
            Quantity: "",
            Rate: woIncl,
            ExclAmount: woSplit.excl,
            VatAmount: woSplit.vat,
            Total: woIncl,
            Patient: patientName,
            ServiceProvider: "",
            PracticeName: practiceName,
          });
        });

        return acc;
      }, []);

      let rows = exportData;
      let sheetName = "Transactions";
      let fileName = "transaction-report.xlsx";
      if (exportType === "summary") {
        rows = exportData.filter((r) => r.RowType === "Invoice");
        sheetName = "Transactions Summary";
        fileName = "transaction-report-summary.xlsx";
      } else if (exportType === "lines") {
        rows = exportData.filter((r) => r.RowType === "Line");
        sheetName = "Invoice Lines";
        fileName = "transaction-report-lines.xlsx";
      }

      const ExcelJS = (await import("exceljs")).default;
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet(sheetName);

      const moneyFmt = '#,##0.00;[Red]-#,##0.00;""';

      ws.columns = [
        { header: "Row Type",         key: "RowType",         width: 12 },
        { header: "Date",             key: "Date",            width: 12 },
        { header: "Date of Service",  key: "DateOfService",   width: 14 },
        { header: "Invoice #",        key: "InvoiceNumber",   width: 14 },
        { header: "Account Number",   key: "AccountNumber",   width: 14 },
        { header: "Account Name",     key: "AccountName",     width: 28 },
        { header: "Status",           key: "Status",          width: 14 },
        { header: "Reference",        key: "Reference",       width: 14 },
        { header: "Description",      key: "Description",     width: 38 },
        { header: "Qty",              key: "Quantity",        width: 7  },
        { header: "Rate",             key: "Rate",            width: 13, style: { numFmt: moneyFmt } },
        { header: "Excl Amount",      key: "ExclAmount",      width: 13, style: { numFmt: moneyFmt } },
        { header: "VAT Amount",       key: "VatAmount",       width: 13, style: { numFmt: moneyFmt } },
        { header: "Total",            key: "Total",           width: 13, style: { numFmt: moneyFmt } },
        { header: "Patient",          key: "Patient",         width: 22 },
        { header: "Service Provider", key: "ServiceProvider", width: 22 },
        { header: "Practice Name",    key: "PracticeName",    width: 24 },
      ];

      ws.addRows(rows);

      ws.getRow(1).font = { bold: true };
      ws.views = [{ state: "frozen", ySplit: 1 }];
      ws.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: 1, column: ws.columns.length },
      };

      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, fileName);
    } catch (e) {
      console.error("Export failed:", e);
    } finally {
      setExportLoading(false);
    }
  };

  const openFilters = () => {
    setTmpPractices(selectedPractices);
    setTmpStatuses(selectedStatuses);
    setTmpStartDate(startDate);
    setTmpEndDate(endDate);
    setShowFilters(true);
  };

  const applyFilters = () => {
    setSelectedPractices(tmpPractices);
    setSelectedStatuses(tmpStatuses);
    setStartDate(tmpStartDate);
    setEndDate(tmpEndDate);
    setPage(1);
    setShowFilters(false);

    // Write filters to URL — preserve existing params (e.g. section=) then set/clear filter keys
    const params = new URLSearchParams(location.search);
    params.delete('practices'); params.delete('statuses'); params.delete('start'); params.delete('end');
    if (tmpPractices.length) params.set('practices', tmpPractices.join(','));
    if (tmpStatuses.length) params.set('statuses', tmpStatuses.join(','));
    if (tmpStartDate) params.set('start', tmpStartDate);
    if (tmpEndDate) params.set('end', tmpEndDate);
    navigate({ search: params.toString() }, { replace: true });
  };

  const clearFilters = () => {
    setSelectedPractices([]);
    setSelectedStatuses([]);
    const today = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - 1);
    const iso = (d) => d.toISOString().slice(0, 10);
    const s = iso(start);
    const e = iso(today);
    setStartDate(s);
    setEndDate(e);
    setTmpPractices([]);
    setTmpStatuses([]);
    setTmpStartDate(s);
    setTmpEndDate(e);
    // Preserve section= and other non-filter params
    const params = new URLSearchParams(location.search);
    params.delete('practices'); params.delete('statuses'); params.delete('start'); params.delete('end');
    navigate({ search: params.toString() }, { replace: true });
  };

  const activeCount =
    (selectedPractices.length ? 1 : 0) +
    (selectedStatuses.length ? 1 : 0) +
    (startDate && endDate ? 1 : 0);

  return (
    <Container fluid className="pt-2 acc-list">
      {/* Top bar: Totals (left, 50%) + Filter/Clear/Pagination/Export/Info (right, 50%, bottom-aligned), directly above the data table */}
      {/* DESKTOP TOP-BAR (>= 768px) — Phase 37.1 Plan 02 Fix 3 wrapper.
          The existing Row with Total Transactions Table + action cluster is preserved verbatim. */}
      <div className="d-none d-lg-block">
        <Row className="mb-2">
          <Col md={6}>
            <span className="text-primary fw-semibold">Total Transactions:</span>
            <Table bordered size="sm" className="mb-0 text-center align-middle">
              <thead>
                <tr>
                  <th style={{ width: "33%" }} className="fw-semibold text-primary">Excluding VAT</th>
                  <th style={{ width: "33%" }} className="fw-semibold text-primary">Including VAT</th>
                  <th style={{ width: "34%" }} className="fw-semibold text-primary">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="acc-list-num">{currency.format(totals.sumExcl)}</td>
                  <td className="acc-list-num">{currency.format(totals.sumVat)}</td>
                  <td className="acc-list-num"><strong>{currency.format(totals.sumTotal)}</strong></td>
                </tr>
              </tbody>
            </Table>
          </Col>
          <Col md={6} className="d-flex gap-2 flex-wrap justify-content-end align-self-end">
            <Button variant="primary" onClick={openFilters} style={{ width: "120px" }}>
              <i className="fa-solid fa-filter me-2" />
              Filter {activeCount > 0 && <Badge className="ms-2" bg="light" text="dark">{activeCount}</Badge>}
            </Button>
            <Button
              variant="outline-danger"
              onClick={clearFilters}
              style={{ width: "120px" }}
              disabled={
                selectedPractices.length === 0 &&
                selectedStatuses.length === 0 &&
                !startDate &&
                !endDate
              }
            >
              <i className="fa-solid fa-trash me-2" />
              Filters
            </Button>

            {/* Pagination */}
            <div className="d-flex align-items-center gap-2 ms-2">
              <Button
                variant="primary"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <i className="fa-solid fa-angle-left" />
              </Button>
              <div className="text-nowrap small">Page {page} of {totalPages}</div>
              <Button
                variant="primary"
                disabled={page === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                <i className="fa-solid fa-angle-right" />
              </Button>
            </div>

            {/* Export + Info */}
            <ButtonGroup className="ms-1 gap-1">
              <OverlayTrigger placement="bottom" overlay={<Tooltip>Export to Excel</Tooltip>}>
                <Button variant="outline-secondary" onClick={() => setShowExportModal(true)} disabled={exportLoading}>
                  {exportLoading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <i className="fa-solid fa-file-export" />
                  )}
                </Button>
              </OverlayTrigger>
              <OverlayTrigger placement="bottom" overlay={<Tooltip>Info</Tooltip>}>
                <Button variant="outline-secondary" onClick={() => setShowInfo(true)}>
                  <i className="fa-solid fa-circle-info" />
                </Button>
              </OverlayTrigger>
            </ButtonGroup>
          </Col>
        </Row>
      </div>

      {/* MOBILE TOP-BAR (< 768px) — Phase 37.1 Plan 02 Fix 3.
          Row 1: compact totals summary mirroring InvoiceList mobile layout.
          Row 2: action cluster. */}
      <div className="d-lg-none mb-2">
        {/* Mobile totals — same 4-column grid as InvoiceList for visual parity.
            Total row uses left pair only; right pair stays empty to keep column alignment. */}
        <div className="acc-list-mobile-summary mb-2">
          <span className="acc-list-mobile-summary-label">Excl Amount</span>
          <span className="acc-list-mobile-summary-amount">{currency.format(totals.sumExcl)}</span>
          <span className="acc-list-mobile-summary-label">VAT Amount</span>
          <span className="acc-list-mobile-summary-amount">{currency.format(totals.sumVat)}</span>

          <span className="acc-list-mobile-summary-label acc-list-mobile-summary-label-total">Total</span>
          <span className="acc-list-mobile-summary-amount acc-list-mobile-summary-amount-total">{currency.format(totals.sumTotal)}</span>
          <span />
          <span />
        </div>
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <Button
            variant="primary"
            onClick={openFilters}
            className="flex-grow-1"
            style={{ minWidth: 110 }}
          >
            <i className="fa-solid fa-filter me-2" />
            Filter {activeCount > 0 && <Badge className="ms-2" bg="light" text="dark">{activeCount}</Badge>}
          </Button>
          <Button
            variant="outline-danger"
            onClick={clearFilters}
            className="flex-grow-1"
            style={{ minWidth: 110 }}
            disabled={
              selectedPractices.length === 0 &&
              selectedStatuses.length === 0 &&
              !startDate &&
              !endDate
            }
          >
            <i className="fa-solid fa-trash me-2" />
            Filters
          </Button>
          <div className="d-flex align-items-center gap-2 ms-auto">
            <Button
              variant="primary"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <i className="fa-solid fa-angle-left" />
            </Button>
            <span className="text-nowrap small">Page {page} of {totalPages}</span>
            <Button
              variant="primary"
              disabled={page === totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              <i className="fa-solid fa-angle-right" />
            </Button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading Transactions...</p>
        </div>
      ) : displayRows.length === 0 ? (
        <p>No transactions found. The default filter is for the last 7 days.</p>
      ) : (
        <>
        <div className="d-none d-lg-block">
        <Table bordered hover responsive className="fixed-table">
          <thead className="text-center align-middle ms-1 me-1">
            <tr>
              <th style={{ width: "4%" }}></th>
              <th
                style={{ cursor: "pointer", width: "8%" }}
                className="text-primary fw-semibold"
                onClick={() => handleSort("invoiceDate")}
              >
                Date {renderSortIcon("invoiceDate")}
              </th>
              <th style={{ width: "8%" }} className="text-primary fw-semibold">Inv Number</th>
              <th style={{ width: "20%" }} className="text-primary fw-semibold">Practice Name</th>
              <th style={{ width: "10%" }} className="text-primary fw-semibold">Acc Number</th>
              <th
                style={{ cursor: "pointer", width: "18%" }}
                onClick={() => handleSort("accountName")}
                className="text-primary fw-semibold"
              >
                Account Name {renderSortIcon("accountName")}
              </th>
              <th
                style={{ cursor: "pointer", width: "18%" }}
                onClick={() => handleSort("status")}
                className="text-primary fw-semibold"
              >
                Status {renderSortIcon("status")}
              </th>
              <th style={{ width: "10%" }} className="text-primary fw-semibold">*Excl Amount</th>
              <th style={{ width: "10%" }} className="text-primary fw-semibold">*VAT Amount</th>
              <th
                style={{ cursor: "pointer", width: "10%" }}
                onClick={() => handleSort("totalAmount")}
                className="text-primary fw-semibold"
              >
                Total {renderSortIcon("totalAmount")}
              </th>
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row) => {
              if (row.type === "invoice") {
                const inv = row.inv;
                const isExpanded = expandedRows.has(inv._id);
                const creditNotes = (inv.receipts || []).filter((r) => r.creditNoteNumber);
                const writeOffs = (inv.receipts || []).filter((r) => r.writeOffNumber);
                const totalCN = creditNotes.reduce((s, r) => s + (r.creditNoteAmount || 0), 0);
                const totalWO = writeOffs.reduce((s, r) => s + (r.writeOffAmount || 0), 0);
                const adjustedTotal = (inv.totalAmount || 0) - totalCN - totalWO;
                return (
                  <React.Fragment key={`inv-${inv._id}`}>
                    <tr
                      style={{
                        cursor: "pointer",
                        "--bs-table-bg": isExpanded ? "#CFE3FF4F" : "",
                        "--bs-table-color": isExpanded ? "#0f3490" : "",
                        ...(isExpanded
                          ? { color: "#cfe2ff", borderTop: "1px solid #0f3490", fontWeight: 500 }
                          : {}),
                      }}
                      onClick={() => toggleRow(inv._id)}
                    >
                      <td className="text-center align-middle">
                        <i className={`fa-solid fa-chevron-${isExpanded ? "down" : "right"} text-primary`} />
                      </td>
                      <td>{inv.invoiceDate ? new Date(inv.invoiceDate).toLocaleDateString() : ""}</td>
                      <td
                        style={{ cursor: "pointer" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(
                            inv.status === "Pending"
                              ? `/invoices/${inv._id}/${inv.accountId}`
                              : `/invoices/posted/${inv._id}`,
                            inv.status !== "Pending"
                              ? { state: { breadcrumbTrail: [{ label: 'Transaction List', path: `${location.pathname}${location.search}` }] } }
                              : undefined
                          );
                        }}
                      >
                        {inv.invoiceNumber}
                      </td>
                      <td>{inv.practice?.practiceName}</td>
                      <td>{inv.accountNumber || ""}</td>
                      <td
                        style={{ cursor: "pointer" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/accounts/${inv.accountId}`);
                        }}
                      >
                        {inv.accountName}
                      </td>
                      <td>{inv.status}</td>
                      <td className="text-end acc-list-num">{inv.exclAmount ? currency.format(inv.exclAmount) : "-"}</td>
                      <td className="text-end acc-list-num">{inv.vatAmount ? currency.format(inv.vatAmount) : "-"}</td>
                      <td className="text-end fw-bold acc-list-num">{currency.format(adjustedTotal)}</td>
                    </tr>
                    {isExpanded && (
                      <tr key={`inv-${inv._id}-lines`}>
                        <td colSpan={2}></td>
                        <td colSpan={7} style={{ padding: "0.25rem 0.5rem" }}>
                          {!inv.lines || inv.lines.length === 0 ? (
                            <span className="text-muted small">No line items</span>
                          ) : (
                            <table
                              className="table table-sm table-borderless mb-0"
                              style={{ fontSize: "0.85rem", tableLayout: "fixed", width: "100%" }}
                            >
                              <colgroup>
                                <col style={{ width: "10%" }} />
                                <col style={{ width: "30%" }} />
                                <col style={{ width: "18%" }} />
                                <col style={{ width: "19%" }} />
                                <col style={{ width: "5%" }} />
                                <col style={{ width: "10%" }} />
                              </colgroup>
                              <tbody>
                                {/* Invoice lines */}
                                {inv.lines.map((line, li) => (
                                  <tr key={li}>
                                    <td className="text-primary">{line.tariffCode || line.nappiCode || "-"}</td>
                                    <td className="text-primary">{line.description || "-"}</td>
                                    <td className="text-primary">
                                      {inv.patient?.patientFirstName
                                        ? `${inv.patient.patientFirstName} ${inv.patient.patientLastName || ""}`.trim()
                                        : "-"}
                                    </td>
                                    <td className="text-primary">
                                      {line.serviceProvider
                                        ? `${line.serviceProvider.firstName} ${line.serviceProvider.lastName}`.trim()
                                        : inv.practitioner?.firstName
                                          ? `${inv.practitioner.firstName} ${inv.practitioner.lastName || ""}`.trim()
                                          : "-"}
                                    </td>
                                    <td className="text-center text-primary">{line.quantity ?? 1}</td>
                                    <td className="text-end text-primary">{currency.format(line.rate || 0)}</td>
                                  </tr>
                                ))}
                                {/* Credit note lines */}
                                {creditNotes.map((r, ci) => (
                                  <tr key={`cn-${ci}`} style={{ color: "#6c757d" }}>
                                    <td colSpan={4} style={{ fontSize: "0.8rem", fontStyle: "italic" }}>
                                      Credit Note {r.creditNoteNumber}{r.creditNoteDate ? ` — ${new Date(r.creditNoteDate).toLocaleDateString()}` : ""}
                                    </td>
                                    <td colSpan={1}></td>
                                    <td className="text-end text-danger">({currency.format(r.creditNoteAmount || 0)})</td>
                                  </tr>
                                ))}
                                {/* Write-off lines */}
                                {writeOffs.map((r, wi) => (
                                  <tr key={`wo-${wi}`} style={{ color: "#6c757d" }}>
                                    <td colSpan={4} style={{ fontSize: "0.8rem", fontStyle: "italic" }}>
                                      Write Off {r.writeOffNumber}{r.writeOffDate ? ` — ${new Date(r.writeOffDate).toLocaleDateString()}` : ""}
                                    </td>
                                    <td colSpan={1}></td>
                                    <td className="text-end text-danger">({currency.format(r.writeOffAmount || 0)})</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              }
            })}
          </tbody>
        </Table>
        </div>
        {/* MOBILE CARD BRANCH (Phase 37) — FLAT cards, filters to row.type === "invoice" */}
        <div className="d-lg-none">
          {displayRows.map((row) => {
            if (row.type !== "invoice") return null;
            const inv = row.inv;
            const isExpanded = expandedRows.has(inv._id);
            // LIFT verbatim from TransactionList.jsx desktop branch — note `totalAmount` NOT `total`
            const creditNotes = (inv.receipts || []).filter((r) => r.creditNoteNumber);
            const writeOffs = (inv.receipts || []).filter((r) => r.writeOffNumber);
            const totalCN = creditNotes.reduce((s, r) => s + (r.creditNoteAmount || 0), 0);
            const totalWO = writeOffs.reduce((s, r) => s + (r.writeOffAmount || 0), 0);
            const adjustedTotal = (inv.totalAmount || 0) - totalCN - totalWO;

            return (
              <div className="acc-card" key={`inv-${inv._id}`}>
                <div className="acc-card-top">
                  <span className="acc-card-id">{inv.invoiceNumber}</span>
                  {/* NO `> 0` guard — preserved per file convention (37-RESEARCH OQ-1) */}
                  <span className="acc-card-amount">{currency.format(adjustedTotal)}</span>
                  <Dropdown align="end" onClick={(e) => e.stopPropagation()}>
                    <Dropdown.Toggle
                      variant="link"
                      className="acc-card-kebab text-primary text-decoration-none p-0 border-0"
                      aria-label="Row actions"
                    >
                      <i className="fa-solid fa-ellipsis-vertical" />
                    </Dropdown.Toggle>
                    <Dropdown.Menu
                      align="end"
                      className="shadow-sm"
                    >
                      {/* Open Invoice — LIFT verbatim from TransactionList desktop branch */}
                      <Dropdown.Item
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(
                            inv.status === "Pending"
                              ? `/invoices/${inv._id}/${inv.accountId}`
                              : `/invoices/posted/${inv._id}`,
                            inv.status !== "Pending"
                              ? { state: { breadcrumbTrail: [{ label: 'Transaction List', path: `${location.pathname}${location.search}` }] } }
                              : undefined
                          );
                        }}
                      >
                        <i className="fa-solid fa-file-invoice me-2" /> Open Invoice
                      </Dropdown.Item>
                      {/* Open Account — LIFT verbatim from TransactionList desktop branch.
                          INTENTIONALLY no second-arg state on navigate (per 37-RESEARCH OQ-1 — do NOT harmonize with InvoiceList) */}
                      <Dropdown.Item
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/accounts/${inv.accountId}`);
                        }}
                      >
                        <i className="fa-solid fa-user me-2" /> Open Account
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>

                <div
                  className="acc-card-body"
                  onClick={() => toggleRow(inv._id)}
                  role="button"
                  tabIndex={0}
                >
                  <div className="acc-card-field">
                    <span className="acc-card-label">Date</span>
                    <span className="acc-card-value">
                      {inv.invoiceDate ? new Date(inv.invoiceDate).toLocaleDateString() : ""}
                    </span>
                  </div>
                  <div className="acc-card-field">
                    <span className="acc-card-label">Practice</span>
                    <span className="acc-card-value">{inv.practice?.practiceName}</span>
                  </div>
                  <div className="acc-card-field">
                    <span className="acc-card-label">Account #</span>
                    <span className="acc-card-value">{inv.accountNumber || ""}</span>
                  </div>
                  <div className="acc-card-field">
                    <span className="acc-card-label">Account</span>
                    <span className="acc-card-value">{inv.accountName}</span>
                  </div>
                  <div className="acc-card-field">
                    <span className="acc-card-label">Status</span>
                    <span className="acc-card-value">
                      <Badge bg="secondary">{inv.status}</Badge>
                    </span>
                  </div>

                  <div className="acc-card-expand-hint">
                    <i className={`fa-solid fa-chevron-${isExpanded ? "down" : "right"} text-primary`} />
                    <span className="ms-2 small text-muted">
                      {isExpanded ? "Hide line items" : "Show line items"}
                    </span>
                  </div>
                </div>

                {isExpanded && (
                  <div className="acc-card-lines">
                    {(inv.lines || []).map((line, li) => (
                      <div className="acc-card-line" key={`line-${li}`}>
                        <span className="acc-card-line-code">{line.tariffCode || "-"}</span>
                        <span className="acc-card-line-rate">{currency.format(line.rate || 0)}</span>
                        <span className="acc-card-line-desc">{line.description || "-"}</span>
                      </div>
                    ))}
                    {creditNotes.map((r, ci) => (
                      <div className="acc-card-line" key={`cn-${ci}`}>
                        <span className="acc-card-line-code">CN {r.creditNoteNumber}</span>
                        <span className="acc-card-line-rate">{currency.format(-(r.creditNoteAmount || 0))}</span>
                      </div>
                    ))}
                    {writeOffs.map((r, wi) => (
                      <div className="acc-card-line" key={`wo-${wi}`}>
                        <span className="acc-card-line-code">WO {r.writeOffNumber}</span>
                        <span className="acc-card-line-rate">{currency.format(-(r.writeOffAmount || 0))}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        </>
      )}

      {/* Export type modal */}
      <Modal show={showExportModal} onHide={() => setShowExportModal(false)} centered>
        <Modal.Header
          closeButton
          style={{ background: "#CFE2FF" }}
          className="rounded-0 text-primary d-flex align-items-center"
        >
          <Modal.Title as="h6" className="m-0">
            Export to Excel
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">Choose what to include in the spreadsheet:</p>
          <div className="d-grid gap-2">
            <Button
              variant="outline-primary"
              className="text-start"
              onClick={() => handleExportExcel("summary")}
            >
              <div className="fw-semibold">Summary</div>
              <small>One row per invoice — Excl, VAT and Total only.</small>
            </Button>
            <Button
              variant="outline-primary"
              className="text-start"
              onClick={() => handleExportExcel("lines")}
            >
              <div className="fw-semibold">Invoice Lines</div>
              <small>Just the billed line items across all invoices.</small>
            </Button>
            <Button
              variant="outline-primary"
              className="text-start"
              onClick={() => handleExportExcel("full")}
            >
              <div className="fw-semibold">Invoices with Lines</div>
              <small>Full report — invoices, lines, credit notes and write-offs.</small>
            </Button>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowExportModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Info modal */}
      <Modal show={showInfo} onHide={() => setShowInfo(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Transaction Report</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-muted">
          Use the filter panel to refine the dataset by date range, practice, and status.
          Totals reflect all filtered rows (not just this page). Excel export downloads the entire filtered dataset.
          Credit notes are shown as negative amounts in brackets. <strong>*Excluding</strong> and <strong>*VAT</strong> amounts are only populated for practices registered for VAT.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowInfo(false)}>Got it</Button>
        </Modal.Footer>
      </Modal>

      {/* Filter Offcanvas */}
      <Offcanvas show={showFilters} onHide={() => setShowFilters(false)} placement="start">
        <Offcanvas.Header closeButton>
          <Offcanvas.Title className="d-flex align-items-center">
            <i className="fa-solid fa-filter me-2 text-primary" />
            Filters
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {/* Date range */}
          <div className="mb-3">
            <h6 className="text-primary fw-normal ms-3 mb-2">Date range</h6>
            <div className="d-flex gap-2 ms-1">
              <Form.Control
                type="date"
                value={tmpStartDate}
                onChange={(e) => setTmpStartDate(e.target.value)}
              />
              <Form.Control
                type="date"
                value={tmpEndDate}
                onChange={(e) => setTmpEndDate(e.target.value)}
              />
            </div>
          </div>

          <Accordion alwaysOpen>
            {/* Practices */}
            <Accordion.Item eventKey="0">
              <Accordion.Header>Practices</Accordion.Header>
              <Accordion.Body>
                <div className="mb-2 d-flex gap-2">
                  <Button size="sm" variant="outline-secondary" onClick={() => setTmpPractices(allPractices)}>
                    Select all
                  </Button>
                  <Button size="sm" variant="outline-secondary" onClick={() => setTmpPractices([])}>
                    Clear
                  </Button>
                </div>
                <div className="d-flex flex-column" style={{ maxHeight: 240, overflowY: "auto" }}>
                  {allPractices.map((name) => (
                    <Form.Check
                      key={name}
                      type="checkbox"
                      id={`practice-${name}`}
                      label={name}
                      checked={tmpPractices.includes(name)}
                      onChange={() => setTmpPractices((arr) => toggleIn(arr, name))}
                    />
                  ))}
                </div>
              </Accordion.Body>
            </Accordion.Item>

            {/* Statuses */}
            <Accordion.Item eventKey="1">
              <Accordion.Header>Status</Accordion.Header>
              <Accordion.Body>
                <div className="mb-2 d-flex gap-2">
                  <Button size="sm" variant="outline-secondary" onClick={() => setTmpStatuses(statusOptions)}>
                    Select all
                  </Button>
                  <Button size="sm" variant="outline-secondary" onClick={() => setTmpStatuses([])}>
                    Clear
                  </Button>
                </div>
                <div className="d-flex flex-column">
                  {statusOptions.map((s) => (
                    <Form.Check
                      key={s}
                      type="checkbox"
                      id={`status-${s}`}
                      label={s}
                      checked={tmpStatuses.includes(s)}
                      onChange={() => setTmpStatuses((arr) => toggleIn(arr, s))}
                    />
                  ))}
                </div>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>

          {/* Actions */}
          <div className="mt-4 d-flex gap-2">
            <Button onClick={applyFilters} style={{ width: "120px" }}>
              <i className="fa-solid fa-filter me-2 text-white" />
              Apply
            </Button>
            <Button
              style={{ width: "120px" }}
              variant="outline-danger"
              onClick={() => {
                setTmpPractices([]);
                setTmpStatuses([]);
                setTmpStartDate("");
                setTmpEndDate("");
              }}
            >
              <i className="fa-solid fa-trash me-2" />
              Remove
            </Button>
          </div>
          <div className="mt-2 text-muted small">
            Tip: leaving a section empty means "All".
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </Container>
  );
}
